'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import th from '../locales/th.json';
import en from '../locales/en.json';

type Language = 'TH' | 'EN';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (path: string, variables?: Record<string, any>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, any> = { TH: th, EN: en };

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('TH');

  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang && (savedLang === 'TH' || savedLang === 'EN')) {
      setLanguageState(savedLang);
    }
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  }, []);

  const t = useCallback((path: string, variables?: Record<string, any>): string => {
    if (!path) return '';
    const keys = path.split('.');
    const currentTranslations = translations[language] || translations['TH'];
    
    let result = currentTranslations;
    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        return path;
      }
    }
    
    if (typeof result !== 'string') return path;

    // Handle string interpolation
    if (variables) {
      Object.entries(variables).forEach(([key, value]) => {
        result = (result as string).replace(`{${key}}`, String(value));
      });
    }
    
    return result;
  }, [language]);

  const contextValue = useMemo(() => ({
    language,
    setLanguage,
    t
  }), [language, setLanguage, t]);

  // Prevent hydration mismatch by only rendering children after mount
  // Or at least ensure we use the same language on server and client initial render
  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
